import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import Head from 'src/components/head';
import { useGetExciseRegister } from 'src/api/public';

interface TableRowData {
  num: number;
  dataIntrod: string;
  tipoDoc: string;
  dataDoc: string;
  fornitore: string;
  numeroDoc: string;
  prodotto: string;
  ltIdratiCarico: number | null;
  ltIdratiScarico: number | null;
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ExciseReport() {
  const query = useQuery();
  const date = query.get('date') || format(new Date(), 'yyyy-MM-dd');

  const { data } = useGetExciseRegister(date);

  // Format date for display
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  // Build table rows from API data
  const buildTableRows = (): TableRowData[] => {
    if (!data) return [];

    const rows: TableRowData[] = [];
    let rowNum = 1;

    // Opening balance (Giacenza) - always show
    rows.push({
      num: rowNum,
      dataIntrod: formatDate(date),
      tipoDoc: 'Giacenza',
      dataDoc: formatDate(date),
      fornitore: '',
      numeroDoc: '',
      prodotto: 'W200/W300',
      ltIdratiCarico: data.openingBalanceLitres,
      ltIdratiScarico: null,
    });
    rowNum += 1;

    // Credit movements (EAD)
    data.creditMovements.forEach((movement) => {
      rows.push({
        num: rowNum,
        dataIntrod: formatDate(date),
        tipoDoc: 'EAD',
        dataDoc: formatDate(date),
        fornitore: movement.producerDocNumber,
        numeroDoc: movement.eadDocumentNumber,
        prodotto: 'W200/W300',
        ltIdratiCarico: movement.litres,
        ltIdratiScarico: null,
      });
      rowNum += 1;
    });

    // Debit movements (DDT) - sum all debits into a single line
    if (data.debitMovements.length > 0) {
      const totalDebitLitres = data.debitMovements.reduce(
        (sum, movement) => sum + movement.litres,
        0,
      );

      rows.push({
        num: rowNum,
        dataIntrod: formatDate(date),
        tipoDoc: 'DDT',
        dataDoc: formatDate(date),
        fornitore: '',
        numeroDoc: 'vari',
        prodotto: 'W200/W300',
        ltIdratiCarico: null,
        ltIdratiScarico: totalDebitLitres,
      });
    }

    return rows;
  };

  const tableRows = buildTableRows();
  const totalLt = data?.closingBalanceLitres || 0;

  return (
    <>
      <Head title="Destinatario Registrato Vino - Registro delle partite movimentate" />

      {data && (
        <Box sx={{ p: 4, bgcolor: '#fff', minHeight: '100vh' }} className="page-container">
          {/* Header with company info */}
          {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Typography variant="body2" sx={{ textAlign: 'right', lineHeight: 1.6 }}>
              Xpitality Srl, P.IVA 08739280967, via dei Mille 5, 20098 San Giuliano Milanese -
              Licenza IT00MIV00128A
            </Typography>
          </Box> */}

          {/* <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1, visibility: 'hidden' }}>
              Destinatario Registrato Vino
            </Typography>
            <Typography variant="h5" sx={{ mb: 3, visibility: 'hidden' }}>
              Registro delle partite movimentate
            </Typography>
          </Box> */}

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6">MOVIMENTAZIONI DEL {formatDate(date)}</Typography>
          </Box>

          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{ border: '1px solid #000', boxShadow: 'none', borderRadius: 0, marginTop: '90px' }}
          >
            <Table
              sx={{
                '& .MuiTableCell-root': {
                  border: '1px solid #000',
                  padding: '8px',
                  bgcolor: 'transparent',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ bgcolor: 'transparent' }}>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Num
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Data introd.
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Tipo doc
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Data doc.
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Fornitore
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Numero doc.
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Prodotto
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Lt. idrati carico
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Lt. Idrati scarico
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row) => (
                  <TableRow key={row.num}>
                    <TableCell align="center">{row.num}</TableCell>
                    <TableCell align="center">{row.dataIntrod}</TableCell>
                    <TableCell align="center">{row.tipoDoc}</TableCell>
                    <TableCell align="center">{row.dataDoc}</TableCell>
                    <TableCell align="center">{row.fornitore}</TableCell>
                    <TableCell align="center">{row.numeroDoc}</TableCell>
                    <TableCell align="center">{row.prodotto}</TableCell>
                    <TableCell align="center">{row.ltIdratiCarico || ''}</TableCell>
                    <TableCell align="center">{row.ltIdratiScarico || ''}</TableCell>
                  </TableRow>
                ))}

                {/* Total row */}
                <TableRow>
                  <TableCell colSpan={6}>&nbsp;</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    Giacenza
                  </TableCell>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Totale Lt.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6}>&nbsp;</TableCell>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell>&nbsp;</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {totalLt}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </>
  );
}

export default ExciseReport;
